/**
 * The calendar's questions about a move — Aaron, Oct 24:
 *
 * - **Just this one · Every one** (small question 3: "yes"), asked when a
 *   repeating item is dropped, resized or rescheduled, as Outlook asks it.
 * - **Move this one** — *Reschedule* on one occurrence: its day, its start
 *   and its length, nothing else of the series.
 * - **Let Renee know?** (note 1.8: "When calendar items get moved that are
 *   connected to a contact / property, it should offer to send them an email
 *   as an activity that shows up in their inbox") — after a site visit or a
 *   meeting with a customer has a new time. A meeting's opens New text or New
 *   email with the words started; a site visit's is folder 48's own
 *   confirmation in its book mode (*Text Renee a confirmation*, *Reply C to
 *   confirm*), so a visit has one confirmation and not two. Nothing is sent
 *   until the person presses **Send** (or **Book**) there.
 */
import { useContext, useEffect, useRef, useState } from 'react'
import {
  MEETING_LENGTHS, clock, lengthWords, monthDay, weekdayShort, whichWords, type CalendarItem, type OccurrencePlace,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { ActivityContext, useActivity } from '../activity-context.js'
import { DayField, TimeField } from '../new/when.js'
import { MenuField } from '../new/kit.js'
import { FloatingCard, type StopCardAnswer } from './overlays.js'
import type { MenuAt } from './item-menu.js'
import { tellQuestion } from './tell.js'
import { SheetAction, StopSheet } from '../stop/sheet.js'

const BTN = 'inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-3 text-[12.5px] font-semibold'
/** The same at a finger's 44 px, for a phone. */
const TOUCH_BTN = 'inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-[14px] font-semibold'

/** A panel by a point, kept inside the window, Escape to cancel. */
function PointPanel({ at, width, label, onClose, children, role = 'dialog' }: {
  at: MenuAt; width: number; label: string; onClose: () => void; children: React.ReactNode; role?: 'dialog' | 'alertdialog'
}) {
  const box = useRef<HTMLDivElement>(null)
  const [place, setPlace] = useState({ left: at.x, top: at.y })
  useEffect(() => {
    const height = box.current?.offsetHeight ?? 120
    setPlace({
      left: Math.max(8, Math.min(window.innerWidth - width - 8, at.x + 8)),
      top: Math.max(8, Math.min(window.innerHeight - height - 8, at.y + 8)),
    })
    box.current?.querySelector<HTMLButtonElement>('[data-first]')?.focus()
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    /* A press anywhere else answers it as Cancel: the block goes back where it was. */
    const onPress = (event: PointerEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) onClose()
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPress, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPress, true)
    }
  }, [at.x, at.y, width, onClose])
  return (
    <div ref={box} role={role} aria-label={label} data-overlay-layer={46}
      className="fixed z-[70] rounded-xl border border-line bg-surface p-3 text-[12.5px] leading-[normal] shadow-popover"
      style={{ left: place.left, top: place.top, width }}>
      {children}
    </div>
  )
}

export type WhichVerb = 'move' | 'resize' | 'reschedule' | 'edit'

const QUESTION: Record<WhichVerb, string> = {
  move: 'Move',
  resize: 'Change',
  reschedule: 'Reschedule',
  edit: 'Edit',
}

/**
 * What *Edit* on a repeating meeting does with each answer (Aaron, Oct 25,
 * phone note 4): both open the meeting's sheet. *Just this one* is that
 * occurrence's — it "should be able to change pretty much anything" (Aaron,
 * Oct 27, answer 12; 0152), so its time and everything else, saved for it
 * alone; *Every one* is the series', and an occurrence changed alone keeps
 * what it said.
 */
export function editWhichLines(item: Pick<CalendarItem, 'date'>): { one: string; all: string } {
  return {
    one: `${weekdayShort(item.date)}, ${monthDay(item.date)} alone: its time, title, who’s going, where, what it’s attached to and notes`,
    all: 'its title, who’s going, where, what it’s attached to and how it repeats — one changed alone keeps its own',
  }
}

/**
 * What each answer will do, for a drag or a resize — or null when the item
 * says nothing of its series, or nothing has been put anywhere yet (the
 * menu's *Reschedule* asks before its card says where).
 */
export function whichLines(
  item: Pick<CalendarItem, 'series' | 'occurrence' | 'date' | 'start' | 'end'>, to: OccurrencePlace | null,
): { one: string; all: string } | null {
  if (!item.series || !to) return null
  const was = { date: item.date, start: item.start, minutes: item.end - item.start }
  return whichWords(item.series.first, item.series.recurrence, item.occurrence ?? item.date, was, to)
}

/**
 * *Move Pipeline review?* — it repeats: **Just this one** or **Every one**
 * (Outlook's two). Cancel puts a dragged block back where it was. Dropped or
 * resized, it says what each will do (Aaron, Oct 25, answer 2.1): *Every one
 * — keeps Mon–Fri, moves to 2:00 PM* for a series whose pattern names its
 * weekdays, *Every one — every Wednesday at 2:00 PM* for one that moves.
 */
export function WhichQuestion({ item, verb, at, to = null, onPick, onCancel }: {
  item: Pick<CalendarItem, 'title' | 'kind' | 'series' | 'occurrence' | 'date' | 'start' | 'end'>; verb: WhichVerb; at: MenuAt
  /** Where it was dropped, or what it was resized to. */
  to?: OccurrencePlace | null
  onPick: (which: 'one' | 'all') => void; onCancel: () => void
}) {
  const every = item.kind === 'block' ? 'Every week' : 'Every one'
  const lines = verb === 'edit' ? editWhichLines(item) : whichLines(item, to)
  return (
    <PointPanel at={at} width={300} label={`${QUESTION[verb]} ${item.title}`} onClose={onCancel}>
      <b className="block text-[13px]">{QUESTION[verb]} {item.title}?</b>
      <span className="mt-0.5 block text-text-muted">It repeats — this one alone, or every one?</span>
      {lines && (
        <ul className="mt-2 flex flex-col gap-0.5 text-[12px] text-text-muted" data-which-lines>
          <li><b className="font-semibold text-text">Just this one</b> — {lines.one}</li>
          <li><b className="font-semibold text-text">{every}</b> — {lines.all}</li>
        </ul>
      )}
      <div className="mt-2.5 flex flex-wrap justify-end gap-1.5">
        <button type="button" className={cn(BTN, 'border-line bg-surface')} onClick={onCancel}>Cancel</button>
        <button type="button" data-first className={cn(BTN, 'border-line bg-surface')} onClick={() => onPick('one')}>Just this one</button>
        <button type="button" className={cn(BTN, 'border-accent bg-accent text-white')} onClick={() => onPick('all')}>{every}</button>
      </div>
    </PointPanel>
  )
}

/**
 * *Just this one · Every one* on a phone — the stop page's *Edit* or
 * *Reschedule* on a repeating meeting (Aaron, Oct 25, phone note 4): the
 * desktop's question as a bottom sheet, each answer a row a thumb can take,
 * saying under it what it will open. A repeat a held finger moved or
 * resized on the phone's calendar asks it too (Aaron, Oct 27, note 1.1.4),
 * saying what each answer will do with where it was put (`whichLines`).
 */
export function WhichSheet({ title, verb, date, lines: said, every = 'Every one', onPick, onClose }: {
  title: string; verb: WhichVerb; date: string
  /** A move's or a resize's own words for each answer (`whichLines`). */
  lines?: { one: string; all: string } | null
  /** *Every week* for a time block. */
  every?: string
  onPick: (which: 'one' | 'all') => void; onClose: () => void
}) {
  const lines = said ?? (verb === 'edit' ? editWhichLines({ date }) : verb === 'reschedule' ? {
    one: `${weekdayShort(date)}, ${monthDay(date)}: its day, start and length`,
    all: 'the day and time of every one, in the meeting’s sheet',
  } : { one: `${weekdayShort(date)}, ${monthDay(date)} only`, all: 'every occurrence moves by as much' })
  const heading = `${QUESTION[verb]} ${title}?`
  return (
    <StopSheet title={heading} label={heading} sub="It repeats — this one alone, or every one?" onClose={onClose}>
      <div className="flex flex-col" data-which-lines>
        {([['one', 'Just this one', lines.one], ['all', every, lines.all]] as const).map(([which, label, sub]) => (
          <button key={which} type="button" onClick={() => onPick(which)}
            className="flex min-h-[60px] w-full items-center gap-3 border-b border-line-faint py-2 text-left last:border-b-0">
            <span className="flex min-w-0 flex-1 flex-col">
              <b className="text-[15px]">{label}</b>
              <span className="text-[12.5px] text-text-muted">{sub}</span>
            </span>
            <span className="inline-flex text-text-faint"><Glyph name="chevronRight" size={18} /></span>
          </button>
        ))}
      </div>
    </StopSheet>
  )
}

/** How long a block may be made here: the meeting lengths, and its own when it is none of them. */
function lengthsFor(minutes: number): number[] {
  return [...new Set([...MEETING_LENGTHS, minutes])].filter((one) => one >= 5 && one <= 1440).sort((a, b) => a - b)
}

/** The day, the start and the length one occurrence (or a time block) is given, and its **Save**. */
/** What a move card reads of the item: its name, where it is, and the series it is one of. */
type MoveItem = Pick<CalendarItem, 'title' | 'date' | 'start' | 'end' | 'series' | 'occurrence'>

function useMoveDraft(item: MoveItem, mode: 'one' | 'all' | 'plain', onSave: MoveSave, onClose: () => void) {
  const [date, setDate] = useState(item.date)
  const [start, setStart] = useState(item.start)
  const [minutes, setMinutes] = useState(item.end - item.start)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /* Every one says what it will do with what is in the boxes now (Aaron, Oct 25, answer 2.1). */
  const untouched = date === item.date && start === item.start && minutes === item.end - item.start
  const everyLine = mode !== 'all' ? null
    : (untouched ? null : whichLines(item, { date, start, minutes })?.all) ?? 'every occurrence moves by as much'
  const save = async () => {
    setBusy(true)
    setProblem(null)
    try {
      await onSave({ date, start, minutes })
      onClose()
    } catch (cause) {
      setProblem(cause instanceof Error && cause.message ? cause.message : `${item.title} couldn’t be moved.`)
    } finally {
      setBusy(false)
    }
  }
  const lengths = lengthsFor(minutes).map((one) => ({ value: String(one), label: lengthWords(one) }))
  return { date, setDate, start, setStart, minutes, setMinutes, problem, busy, everyLine, save, lengths }
}

type MoveSave = (place: { date: string; start: number; minutes: number }) => Promise<void>

/** *Move this Pipeline review*, *Move every Pipeline review*, *Move Pipeline review*. */
function moveHeading(item: MoveItem, mode: 'one' | 'all' | 'plain'): string {
  return mode === 'one' ? `Move this ${item.title}` : mode === 'all' ? `Move every ${item.title}` : `Move ${item.title}`
}

/**
 * *Reschedule* on one occurrence, or on a time block (which has no sheet of
 * its own): the day, the start and the length, and **Save**. (*Edit* → *Just
 * this one* is the meeting's whole sheet for that occurrence since Aaron's
 * Oct 27 answer 12.)
 */
export function MoveCard({ item, mode, at, onClose, onSave }: {
  item: CalendarItem
  /** `one`: this occurrence alone; `all`: every one; `plain`: a one-off. */
  mode: 'one' | 'all' | 'plain'
  at: MenuAt
  onClose: () => void
  onSave: MoveSave
}) {
  const { today } = useActivity()
  const draft = useMoveDraft(item, mode, onSave, onClose)
  const anchor = { getBoundingClientRect: () => new DOMRect(at.x, at.y, 1, 1) }
  const heading = moveHeading(item, mode)
  return (
    <FloatingCard anchor={anchor} width={340} onClose={onClose} label={heading}>
      <div className="flex items-center gap-3 border-b border-line-faint px-4 py-3.5">
        <div className="flex min-w-0 flex-col">
          <b className="truncate text-[14.5px]">{heading}</b>
          <span className="text-[12px] text-text-muted">
            {mode === 'one' ? `${weekdayShort(item.date)}, ${monthDay(item.date)} only — the rest stay where they are`
              : draft.everyLine ?? `${weekdayShort(item.date)}, ${monthDay(item.date)} · ${clock(item.start)}`}
          </span>
        </div>
        <span className="flex-1" />
        <button type="button" aria-label="Close" onClick={onClose}
          className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full text-text-muted hover:bg-hover">
          <Glyph name="close" size={16} />
        </button>
      </div>
      <div className="flex flex-col gap-2 px-4 py-3.5">
        <span className="text-[11.5px] font-medium text-text-muted">When</span>
        <div className="flex items-center gap-1.5">
          <DayField label="Day" value={draft.date} onChange={draft.setDate} phone={false} today={today} />
          <TimeField label="Starts" value={draft.start} onChange={(next) => draft.setStart(next ?? draft.start)} phone={false} width={92} />
          <MenuField label="Length" phone={false} width={64} value={String(draft.minutes)} options={draft.lengths}
            onChange={(next) => draft.setMinutes(Number(next))} />
        </div>
        {draft.problem && <span className="text-[12px] text-danger">{draft.problem}</span>}
      </div>
      <div className="flex items-center gap-2.5 border-t border-line bg-surface-alt px-4 py-3">
        <span className="flex-1" />
        <button type="button" className={cn(BTN, 'border-line bg-surface')} onClick={onClose}>Cancel</button>
        <button type="button" disabled={draft.busy} className={cn(BTN, 'border-accent bg-accent text-white disabled:opacity-60')}
          onClick={() => void draft.save()}>
          <Glyph name="check" size={14.5} />Save
        </button>
      </div>
    </FloatingCard>
  )
}

/**
 * The same card on a phone (Aaron, Oct 25, phone note 4): the stop page's
 * *Reschedule* → *Just this one* on a repeating meeting — a bottom sheet with
 * the day, the start and the length as the phone's own pickers, 46 px, and
 * **Cancel · Save** a thumb can reach.
 */
export function MoveSheet({ item, mode, onClose, onSave }: {
  item: MoveItem
  mode: 'one' | 'all' | 'plain'
  onClose: () => void
  onSave: MoveSave
}) {
  const today = useContext(ActivityContext)?.today ?? item.date
  const draft = useMoveDraft(item, mode, onSave, onClose)
  const heading = moveHeading(item, mode)
  return (
    <StopSheet title={heading} label={heading} onClose={onClose}
      sub={mode === 'one' ? `${weekdayShort(item.date)}, ${monthDay(item.date)} only — the rest stay where they are`
        : draft.everyLine ?? `${weekdayShort(item.date)}, ${monthDay(item.date)} · ${clock(item.start)}`}
      footer={(
        <>
          <SheetAction onClick={onClose}>Cancel</SheetAction>
          <SheetAction primary disabled={draft.busy} onClick={() => void draft.save()}
            icon={<Glyph name="check" size={16} />}>Save</SheetAction>
        </>
      )}>
      <div className="flex flex-col gap-3">
        <PhoneRow label="Day"><DayField label="Day" value={draft.date} onChange={draft.setDate} phone today={today} /></PhoneRow>
        <PhoneRow label="Starts">
          <TimeField label="Starts" value={draft.start} onChange={(next) => draft.setStart(next ?? draft.start)} phone />
        </PhoneRow>
        <PhoneRow label="Length">
          <MenuField label="Length" phone value={String(draft.minutes)} options={draft.lengths}
            onChange={(next) => draft.setMinutes(Number(next))} />
        </PhoneRow>
        {draft.problem && <span role="alert" className="text-[13px] font-semibold text-danger">{draft.problem}</span>}
      </div>
    </StopSheet>
  )
}

function PhoneRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12.5px] font-medium text-text-muted">{label}</span>
      {children}
    </div>
  )
}

/**
 * *Let Renee know?* — at the foot of the calendar after a site visit or a
 * meeting with a customer has a new time (note 1.8). Only ever opens a sheet.
 */
export function TellPrompt({ card, canSend, onMessage, onConfirm, onClose, touch = false }: {
  card: StopCardAnswer
  /** The viewer has an Inbox (§5.3): without one there is nothing to send a text or an email from. */
  canSend: boolean
  /** On a phone, after a held finger moved it (note 1.1.4): its buttons a finger's 44 px. */
  touch?: boolean
  onMessage: (channel: 'sms' | 'email') => void
  /** A site visit: its book mode, where *Text Renee a confirmation* already is. */
  onConfirm: () => void
  onClose: () => void
}) {
  const contact = card.contact!
  const first = contact.firstName || contact.name
  const texts = canSend && Boolean(contact.phone) && !contact.optedOut
  const emails = canSend && Boolean(contact.email)
  const visit = card.kind === 'appointment' && Boolean(card.job)
  /* It goes by itself after a while — it was only ever a question. */
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const timer = window.setTimeout(() => close.current(), 20_000)
    return () => window.clearTimeout(timer)
  }, [card])
  if (!visit && !texts && !emails) return null
  const button = touch ? TOUCH_BTN : BTN
  return (
    <div role="dialog" aria-label={tellQuestion(first)} data-overlay-layer={46}
      className="fixed bottom-[calc(24px+var(--activity-foot,0px))] left-1/2 z-[80] flex max-w-[calc(100vw-32px)] -translate-x-1/2 flex-wrap items-center gap-x-4 gap-y-2 rounded-[12px] border border-line bg-surface px-4 py-3 text-[13px] leading-[normal] shadow-popover">
      <span className="flex min-w-0 flex-col">
        <b>{tellQuestion(first)}</b>
        <span className="text-[12px] text-text-muted">
          {card.title} is now {weekdayShort(card.date)}, {monthDay(card.date)} at {clock(card.start)}
          {card.end > card.start ? ` · ${lengthWords(card.end - card.start)}` : ''}
        </span>
      </span>
      <span className="flex items-center gap-1.5">
        {visit ? (
          <button type="button" className={cn(button, 'border-accent bg-accent text-white')} onClick={onConfirm}>
            <Glyph name="send" size={13} />Send {first} the new time
          </button>
        ) : (
          <>
            {texts && (
              <button type="button" className={cn(button, 'border-line bg-surface')} onClick={() => onMessage('sms')}>
                <Glyph name="bubble" size={14} />Text {first}
              </button>
            )}
            {emails && (
              <button type="button" className={cn(button, 'border-line bg-surface')} onClick={() => onMessage('email')}>
                <Glyph name="envelope" size={14} />Email {first}
              </button>
            )}
          </>
        )}
        <button type="button" className={cn(button, 'border-transparent bg-transparent text-text-muted')} onClick={onClose}>Not now</button>
      </span>
    </div>
  )
}
